/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */

import classNames from 'classnames';
import { useEffect, useRef } from 'react';
import styles from './modal.module.scss';

export interface ModalProps {
  children: React.ReactNode;
  className?: string;
  isCentered?: boolean;
  isOpen?: boolean;
  onClickOutside?: () => void;
  setIsOpen?: (isOpen: boolean) => void;
}

const Modal = ({
  children,
  className,
  isCentered = false,
  isOpen,
  onClickOutside,
  setIsOpen,
}: ModalProps) => {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;

    if (
      !dialog ||
      typeof dialog.showModal !== 'function' ||
      typeof dialog.close !== 'function'
    )
      return;

    if (isOpen) {
      dialog.showModal();
    } else {
      dialog.close();
    }
  }, [isOpen]);

  useEffect(() => {
    const dialog = ref.current;

    if (!dialog) return;

    const handleCancel = () => setIsOpen?.(false);

    const handleClickOutside = (event: MouseEvent) => {
      if (!ref.current) return;

      if (event.target === dialog) {
        onClickOutside?.();
      }
    };

    dialog.addEventListener('cancel', handleCancel);
    dialog.addEventListener('click', handleClickOutside);

    return () => {
      dialog.removeEventListener('cancel', handleCancel);
      dialog.removeEventListener('click', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref]);

  if (!isOpen) return null;

  return (
    <dialog
      ref={ref}
      className={classNames(styles.component, className, {
        [styles.isCentered]: isCentered,
      })}
    >
      {children}
    </dialog>
  );
};

Modal.displayName = 'Modal';

export default Modal;
